<template>
  <div class="ymtc-profile" role="dialog" aria-modal="true" aria-labelledby="ymtc-profile-title">
    <div class="ymtc-profile__backdrop"></div>
    <form class="ymtc-profile__card" @submit.prevent="save">
      <p class="ymtc-profile__kicker">Required to continue</p>
      <h2 id="ymtc-profile-title">Finish your RefReps account</h2>
      <p class="ymtc-profile__lead">
        YMTC access created this account. Add the rest of your details before you use RefReps.
      </p>

      <label v-if="needsPhone">
        <span>Phone</span>
        <input v-model="phone" type="tel" inputmode="tel" autocomplete="tel" placeholder="(864) 555-1212" required />
      </label>
      <label v-if="needsState">
        <span>State</span>
        <select v-model="state" required>
          <option value="">Choose a state</option>
          <option v-for="item in states" :key="item.abbreviation" :value="item.name">
            {{ item.name }}
          </option>
        </select>
      </label>
      <label v-if="needsZip">
        <span>ZIP code</span>
        <input v-model="zipCode" type="text" inputmode="numeric" maxlength="5" placeholder="29601" required />
      </label>
      <label class="ymtc-profile__terms">
        <input v-model="acceptedTerms" type="checkbox" />
        <span>
          I accept the
          <a href="https://www.refreps.com/terms-conditions" target="_blank" rel="noopener noreferrer">terms of service</a>
        </span>
      </label>
      <p v-if="error" class="ymtc-profile__error" role="alert">{{ error }}</p>
      <button type="submit" :disabled="saving || !acceptedTerms">
        {{ saving ? 'Saving…' : 'Save and continue' }}
      </button>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import { mapStores } from 'pinia'
import useMainStore from '@/stores/main'

function digits(value) {
  const raw = String(value || '').replace(/\D/g, '')
  return raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw
}

export default {
  name: 'YmtcProfileCompletionModal',
  data() {
    return {
      phone: '',
      state: '',
      zipCode: '',
      acceptedTerms: false,
      saving: false,
      error: '',
    }
  },
  computed: {
    ...mapStores(useMainStore),
    states() {
      return Array.isArray(this.mainStore.statesArray) ? this.mainStore.statesArray : []
    },
    needsPhone() {
      return digits(this.mainStore.user?.phone).length !== 10
    },
    needsState() {
      return !String(this.mainStore.user?.state || '').trim()
    },
    needsZip() {
      return !/^\d{5}$/.test(String(this.mainStore.user?.zipCode || '').replace(/\D/g, ''))
    },
  },
  mounted() {
    this.phone = this.mainStore.user?.phone || ''
    this.state = this.mainStore.user?.state || ''
    const zip = String(this.mainStore.user?.zipCode || '')
    this.zipCode = /^\d{5}$/.test(zip) ? zip : ''
  },
  methods: {
    async save() {
      this.saving = true
      this.error = ''
      try {
        const { data } = await axios.post(
          `${import.meta.env.VITE_BASE_LINK}/complete-ymtc-profile`,
          {
            phone: this.needsPhone ? this.phone : undefined,
            state: this.needsState ? this.state : undefined,
            zipCode: this.needsZip ? this.zipCode : undefined,
            acceptedTerms: this.acceptedTerms,
          },
          { withCredentials: true },
        )
        if (!data?.success) throw new Error(data?.error || 'Could not save your account.')
        if (this.mainStore.user) {
          this.mainStore.user.ymtcProfileIncomplete = false
          if (data.phone) this.mainStore.user.phone = data.phone
          if (data.state) this.mainStore.user.state = data.state
          if (data.zipCode) this.mainStore.user.zipCode = data.zipCode
          this.mainStore.user.termsAgreement = {
            ...(this.mainStore.user.termsAgreement || {}),
            didAgree: true,
          }
        }
      } catch (error) {
        this.error = error?.response?.data?.error || error.message || 'Could not save your account.'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.ymtc-profile {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: grid;
  place-items: center;
  padding: 20px;
}
.ymtc-profile__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(9, 19, 17, 0.72);
}
.ymtc-profile__card {
  position: relative;
  width: min(480px, 100%);
  border-radius: 24px;
  background: #fffdf8;
  padding: 28px 24px 24px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);
  color: #10211d;
}
.ymtc-profile__kicker {
  margin: 0;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0f766e;
}
.ymtc-profile__card h2 {
  margin: 8px 0 0;
  font-family: Palatino, Georgia, serif;
  font-size: 34px;
  line-height: 1.05;
}
.ymtc-profile__lead {
  margin: 10px 0 18px;
  color: #3f534c;
  line-height: 1.5;
}
.ymtc-profile__card label {
  display: block;
  margin-top: 12px;
}
.ymtc-profile__card label span {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5d726b;
}
.ymtc-profile__card input[type="tel"],
.ymtc-profile__card input[type="text"],
.ymtc-profile__card select {
  width: 100%;
  margin-top: 6px;
  border: 1px solid #d5e0db;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 16px;
}
.ymtc-profile__terms {
  display: flex !important;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px !important;
}
.ymtc-profile__terms span {
  text-transform: none;
  letter-spacing: 0;
  font-size: 14px;
  font-weight: 500;
  color: #10211d;
}
.ymtc-profile__terms a { color: #0f766e; }
.ymtc-profile__error {
  margin: 12px 0 0;
  color: #9f1239;
  font-weight: 600;
}
.ymtc-profile__card button {
  width: 100%;
  margin-top: 16px;
  border: 0;
  border-radius: 14px;
  padding: 14px 16px;
  background: #0f766e;
  color: white;
  font-weight: 800;
  cursor: pointer;
}
.ymtc-profile__card button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
